import { AppState, EditMode, SelectionRef, ZoneRect } from '../types';
import { doLineSegmentsIntersect, isPointInRect } from './geometry';

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
export interface Bounds { x: number; y: number; w: number; h: number }

export type CanvasPointerIntent = 'PAN' | 'MARQUEE' | 'EDIT';

export const getCanvasPointerIntent = ({
  button,
  editMode,
  spacePressed,
  shiftPressed,
  hasEditableTarget
}: {
  button: number;
  editMode: EditMode;
  spacePressed: boolean;
  shiftPressed: boolean;
  hasEditableTarget: boolean;
}): CanvasPointerIntent => {
  if (button === 1 || spacePressed || editMode === 'PAN') return 'PAN';
  if (editMode === 'SELECT' && !hasEditableTarget) return shiftPressed ? 'MARQUEE' : 'PAN';
  return 'EDIT';
};

export const getRectsBounds = (rects: ZoneRect[]): Bounds | null => {
  if (rects.length === 0) return null;
  const minX = Math.min(...rects.map(rect => rect.x));
  const minY = Math.min(...rects.map(rect => rect.y));
  const maxX = Math.max(...rects.map(rect => rect.x + rect.w));
  const maxY = Math.max(...rects.map(rect => rect.y + rect.h));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
};

export const resizeZoneRects = (
  rects: ZoneRect[],
  handle: ResizeHandle,
  dx: number,
  dy: number,
  keepAspect: boolean,
  minSize = 10
): ZoneRect[] => {
  const bounds = getRectsBounds(rects);
  if (!bounds) return rects;
  let left = bounds.x, top = bounds.y, right = bounds.x + bounds.w, bottom = bounds.y + bounds.h;
  if (handle.includes('w')) left = Math.min(right - minSize, left + dx);
  if (handle.includes('e')) right = Math.max(left + minSize, right + dx);
  if (handle.includes('n')) top = Math.min(bottom - minSize, top + dy);
  if (handle.includes('s')) bottom = Math.max(top + minSize, bottom + dy);
  if (keepAspect && handle.length === 2) {
    const ratio = bounds.w / Math.max(1, bounds.h);
    const width = right - left; const height = bottom - top;
    if (width / height > ratio) {
      const targetWidth = height * ratio;
      if (handle.includes('w')) left = right - targetWidth; else right = left + targetWidth;
    } else {
      const targetHeight = width / ratio;
      if (handle.includes('n')) top = bottom - targetHeight; else bottom = top + targetHeight;
    }
  }
  const nextW = right - left; const nextH = bottom - top;
  return rects.map(rect => ({
    x: left + ((rect.x - bounds.x) / Math.max(1, bounds.w)) * nextW,
    y: top + ((rect.y - bounds.y) / Math.max(1, bounds.h)) * nextH,
    w: (rect.w / Math.max(1, bounds.w)) * nextW,
    h: (rect.h / Math.max(1, bounds.h)) * nextH
  }));
};

export const getSelectionBounds = (state: AppState, items: SelectionRef[]): Bounds | null => {
  const selected = new Set(items.map(item => `${item.kind}:${item.id}`));
  const points: Array<{ x: number; y: number }> = [];
  state.nodes.filter(item => selected.has(`NODE:${item.id}`)).forEach(item => points.push({ x: item.x, y: item.y }));
  state.icons.filter(item => selected.has(`ICON:${item.id}`)).forEach(item => points.push({ x: item.x, y: item.y }));
  state.walls.filter(item => selected.has(`WALL:${item.id}`)).forEach(item => points.push({ x: item.x1, y: item.y1 }, { x: item.x2, y: item.y2 }));
  state.paths.filter(item => selected.has(`PATH:${item.id}`)).forEach(item => points.push(...item.points));
  state.zones.filter(item => selected.has(`ZONE:${item.id}`)).forEach(item => item.rects.forEach(rect => points.push({ x: rect.x, y: rect.y }, { x: rect.x + rect.w, y: rect.y + rect.h })));
  if (points.length === 0) return null;
  const minX = Math.min(...points.map(point => point.x)); const minY = Math.min(...points.map(point => point.y));
  const maxX = Math.max(...points.map(point => point.x)); const maxY = Math.max(...points.map(point => point.y));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
};

export const clampSelectionDelta = (bounds: Bounds | null, dx: number, dy: number, width: number, height: number) => bounds ? ({
  dx: Math.max(-bounds.x, Math.min(dx, width - bounds.x - bounds.w)),
  dy: Math.max(-bounds.y, Math.min(dy, height - bounds.y - bounds.h))
}) : ({ dx, dy });

const rectsIntersect = (a: Bounds, b: Bounds) => !(a.x + a.w < b.x || b.x + b.w < a.x || a.y + a.h < b.y || b.y + b.h < a.y);
const segmentIntersectsRect = (a: { x: number; y: number }, b: { x: number; y: number }, rect: Bounds) => {
  if (isPointInRect(a, rect) || isPointInRect(b, rect)) return true;
  const corners = [{ x: rect.x, y: rect.y }, { x: rect.x + rect.w, y: rect.y }, { x: rect.x + rect.w, y: rect.y + rect.h }, { x: rect.x, y: rect.y + rect.h }];
  return corners.some((corner, index) => doLineSegmentsIntersect(a, b, corner, corners[(index + 1) % corners.length]));
};

export const selectItemsInRect = (state: AppState, rect: Bounds): SelectionRef[] => [
  ...state.nodes.filter(node => isPointInRect(node, rect)).map(node => ({ kind: 'NODE' as const, id: node.id })),
  ...state.icons.filter(icon => isPointInRect(icon, rect)).map(icon => ({ kind: 'ICON' as const, id: icon.id })),
  ...state.zones.filter(zone => zone.rects.some(zoneRect => rectsIntersect(rect, zoneRect))).map(zone => ({ kind: 'ZONE' as const, id: zone.id })),
  ...state.walls.filter(wall => segmentIntersectsRect({ x: wall.x1, y: wall.y1 }, { x: wall.x2, y: wall.y2 }, rect)).map(wall => ({ kind: 'WALL' as const, id: wall.id })),
  ...state.paths.filter(path => path.points.some((point, index) => index > 0 && segmentIntersectsRect(path.points[index - 1], point, rect))).map(path => ({ kind: 'PATH' as const, id: path.id }))
];
